import { useEffect, useRef, useState } from "react";
import { listingsApi, type CarVideo } from "@/lib/listings-api";

const INTERVAL = 3000;
const DEADLINE = 90_000;
type Scope = { controller: AbortController; polling: Set<number>; transferring: number | null; rejected: Set<number>; readSequence: number };
export type VideoIssue = "missing" | "unavailable" | "deadline" | "unconfirmed" | "rejected";

/** Every async result belongs to one listing/auth effect, including StrictMode remounts. */
export function useListingVideos(carId: number | null | undefined, token: string | undefined) {
  const [videos, setVideos] = useState<CarVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [readError, setReadError] = useState(false);
  const [issues, setIssues] = useState<Record<number, VideoIssue>>({});
  const scopeRef = useRef<Scope | null>(null);
  const transferLock = useRef(false);
  const live = (scope: Scope) => scopeRef.current === scope && !scope.controller.signal.aborted;
  const signal = (scope: Scope, timeout = 15_000) =>
    AbortSignal.any([scope.controller.signal, AbortSignal.timeout(timeout)]);
  const issue = (id: number, message: VideoIssue) =>
    setIssues(prev => ({ ...prev, [id]: message }));

  async function poll(scope: Scope, id: number) {
    if (!carId || !token || scope.polling.has(id) || !live(scope)) return;
    scope.polling.add(id);
    const end = Date.now() + DEADLINE;
    try {
      while (live(scope) && Date.now() < end) {
        try {
          const video = await listingsApi.refreshVideoStatus(carId, id, token, signal(scope, Math.max(1, Math.min(15_000, end - Date.now()))));
          if (!live(scope)) return;
          if (scope.rejected.has(id) && video.status === "pendingupload") {
            setVideos(prev => prev.map(v => v.id === id ? { ...video, status: "error" } : v));
            issue(id, "rejected");
            return;
          }
          setVideos(prev => prev.map(v => v.id === id ? video : v));
          setIssues(prev => { const next = { ...prev }; delete next[id]; return next; });
          if (video.status === "ready" && video.playback_url || video.status === "error") return;
        } catch (err) {
          if (!live(scope)) return;
          const code = (err as { code?: string }).code;
          if (code === "VIDEO_ROW_MISSING" || code === "VIDEO_ASSET_MISSING") {
            issue(id, "missing");
            return;
          }
          issue(id, "unavailable");
        }
        await new Promise<void>(resolve => {
          const finish = () => {
            clearTimeout(timer);
            scope.controller.signal.removeEventListener("abort", finish);
            resolve();
          };
          const timer = setTimeout(finish, INTERVAL);
          scope.controller.signal.addEventListener("abort", finish, { once: true });
        });
      }
      if (live(scope)) issue(id, "deadline");
    } finally {
      scope.polling.delete(id);
    }
  }

  async function load(scope: Scope) {
    if (!carId || !token || transferLock.current) return;
    const sequence = ++scope.readSequence;
    setLoading(true);
    try {
      const result = await listingsApi.listVideos(carId, token, signal(scope));
      if (!live(scope) || scope.readSequence !== sequence) return;
      setReadError(false);
      setVideos(result.videos.map(v => v.status === "ready" ? { ...v, status: "inprogress", playback_url: null } : v));
      // Reconcile saved ready rows too: a saved DB flag is not current provider evidence.
      for (const video of result.videos) {
        if (video.status !== "error" && video.id !== scope.transferring) void poll(scope, video.id);
      }
    } catch {
      if (live(scope) && scope.readSequence === sequence) setReadError(true); // Preserve known rows; failure is not an empty gallery.
    } finally {
      if (live(scope) && scope.readSequence === sequence) setLoading(false);
    }
  }

  useEffect(() => {
    const scope: Scope = { controller: new AbortController(), polling: new Set(), transferring: null, rejected: new Set(), readSequence: 0 };
    scopeRef.current = scope;
    transferLock.current = false;
    setVideos([]);
    setIssues({});
    setReadError(false);
    setUploading(false);
    setLoading(!!carId && !!token);
    void load(scope);
    return () => { scope.controller.abort(); };
    // All work is bound to this effect's identity, not a reusable cancellation boolean.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId, token]);

  async function upload(file: File, validate: () => Promise<boolean>) {
    const scope = scopeRef.current;
    if (!scope || !carId || !token || transferLock.current || readError || loading) return;
    transferLock.current = true;
    setUploading(true); // Metadata probing also owns the transfer/navigation lock.
    let id: number | undefined;
    try {
      if (!await validate() || !live(scope)) return;
      const mint = await listingsApi.requestVideoUpload(carId, token, signal(scope));
      if (!live(scope)) return;
      id = mint.id;
      scope.transferring = id;
      setVideos(prev => prev.some(v => v.id === id) ? prev : [...prev, mint]);
      const body = new FormData();
      body.append("file", file);
      // Allow the supported 200 MB file on ~512 Kbps upstream, plus overhead.
      // Small clips still have five minutes; provider status requests keep their
      // separate short deadline. An acknowledgement timeout never deletes media.
      const transferTimeout = Math.max(5 * 60_000, Math.ceil(file.size / (64 * 1024)) * 1000 + 120_000);
      const response = await fetch(mint.uploadURL, {
        method: "POST", body, signal: signal(scope, transferTimeout),
      });
      if (!live(scope)) return;
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) scope.rejected.add(id);
        throw new Error(`Video transfer returned ${response.status}.`);
      }
      setVideos(prev => prev.map(v => v.id === id ? { ...v, status: "queued" } : v));
    } catch (err) {
      if (!live(scope)) return;
      if (id !== undefined) {
        // A lost acknowledgement may hide a completed upload. Never auto-delete or re-mint.
        issue(id, "unconfirmed");
      } else {
        setReadError(true);
        throw err;
      }
    } finally {
      if (live(scope)) {
        scope.transferring = null;
        transferLock.current = false;
        setUploading(false);
        if (id !== undefined) void poll(scope, id);
      }
    }
  }

  async function remove(id: number) {
    const scope = scopeRef.current;
    if (!scope || !carId || !token || uploading) return;
    // Pessimistic removal avoids restoring an obsolete full-gallery snapshot.
    try {
      await listingsApi.deleteVideo(carId, id, token, signal(scope));
    } catch (err) {
      if (!live(scope)) return;
      if ((err as { code?: string }).code !== "VIDEO_ROW_MISSING") throw err;
    }
    if (live(scope)) setVideos(prev => prev.filter(v => v.id !== id));
  }

  return {
    videos, loading, uploading, readError, issues, upload, remove,
    retryRead: () => { const scope = scopeRef.current; if (scope) void load(scope); },
    retryStatus: (id: number) => { const scope = scopeRef.current; if (scope) void poll(scope, id); },
  };
}
